import AsyncStorage from '@react-native-async-storage/async-storage';
import { QUICK_REACTION_EMOJIS, REACTION_EMOJIS } from '../data/reactionEmojis';

// Account/device-local preference only: no message, contact, timestamp or content.
type Counts = Record<string, number>;
const queues = new Map<number, Promise<void>>();
const key = (userId: number) => `reaction_frequency:v1:${userId}`;
const validUser = (userId: number) => Number.isSafeInteger(userId) && userId > 0;

async function read(userId: number): Promise<Counts> {
  const raw = await AsyncStorage.getItem(key(userId));
  try {
    const value = JSON.parse(raw ?? '{}');
    const counts: Counts = {};
    if (!value || typeof value !== 'object' || Array.isArray(value)) return counts;
    for (const emoji of REACTION_EMOJIS) {
      const count = value[emoji];
      if (Number.isSafeInteger(count) && count > 0) counts[emoji] = count;
    }
    return counts;
  } catch { return {}; }
}

/** Stable catalog order breaks ties; defaults fill unused positions without duplicates. */
export async function getFrequentReactions(userId: number): Promise<string[]> {
  if (!validUser(userId)) return [...QUICK_REACTION_EMOJIS];
  await queues.get(userId)?.catch(() => {});
  const counts = await read(userId).catch(() => ({} as Counts));
  const used = REACTION_EMOJIS.filter(emoji => counts[emoji] > 0)
    .sort((a, b) => counts[b] - counts[a]);
  return [...new Set([...used, ...QUICK_REACTION_EMOJIS])].slice(0, QUICK_REACTION_EMOJIS.length);
}

/** Call only after an acknowledged add/replace, never a removal or failed request. */
export function recordReactionUse(userId: number, emoji: string): Promise<void> {
  if (!validUser(userId) || !REACTION_EMOJIS.includes(emoji)) return Promise.resolve();
  const next = (queues.get(userId) ?? Promise.resolve()).catch(() => {}).then(async () => {
    const counts = await read(userId);
    counts[emoji] = Math.min(Number.MAX_SAFE_INTEGER, (counts[emoji] ?? 0) + 1);
    await AsyncStorage.setItem(key(userId), JSON.stringify(counts));
  });
  queues.set(userId, next);
  return next.finally(() => { if (queues.get(userId) === next) queues.delete(userId); });
}
